import {ListAltOutlined} from '@mui/icons-material';
import {FC} from 'react';
import {Box, Chip, Typography} from '@mui/material';
import {GridColumns} from '@mui/x-data-grid';
import {FormattedMessage, useIntl} from 'react-intl';
import {ResponsiveTable} from 'src/components/common/ResponsiveTable';
import {EmptyTable} from 'src/components/tables/EmptyTable';
import {Table} from 'src/components/tables/Table';
import {FilterBar} from 'src/components/common/FilterBar';
import {TableLayout} from 'src/components/tables/TableLayout';
import {CurrencyEnum} from 'src/enums';
import {CustomInvoiceItemDTO} from 'src/types';
import {
    DescriptionCell,
    formatPrice,
} from 'src/components/common/CustomInvoiceItemsTable/CustomInvoiceItemsTable.tableConfig';
import {
    priceChipSx,
    qtyCellSx,
} from 'src/components/common/CustomInvoiceItemsTable/CustomInvoiceItemsTable.sx';
import {CardList} from 'src/components/common/CardList';
import {LinkedInvoiceItemCard} from './LinkedInvoiceItemCard';

interface LinkedInvoiceItemsProps {
    items?: CustomInvoiceItemDTO[];
    currency?: CurrencyEnum;
}

const tableSx = {
    '& .MuiDataGrid-cell': {
        alignItems: 'center',
    },
    '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
        outline: 'none !important',
    },
};

const columns = (currency: string): GridColumns => [
    {
        field: 'text',
        renderHeader: () => <FormattedMessage id="description"/>,
        flex: 2,
        minWidth: 220,
        sortable: false,
        cellClassName: 'ampTableCell',
        renderCell: (params) => <DescriptionCell row={params.row as CustomInvoiceItemDTO}/>,
    },
    {
        field: 'quantity',
        renderHeader: () => <FormattedMessage id="quantity"/>,
        width: 90,
        sortable: false,
        cellClassName: 'ampTableCell',
        align: 'center' as const,
        headerAlign: 'center' as const,
        renderCell: (params) => (
            <Typography component="span" sx={qtyCellSx}>
                {(params.row as CustomInvoiceItemDTO).quantity}
            </Typography>
        ),
    },
    {
        field: 'price',
        renderHeader: () => <FormattedMessage id="priceInclVat"/>,
        width: 170,
        sortable: false,
        cellClassName: 'ampTableCell',
        renderCell: (params) => (
            <Chip
                label={`${formatPrice((params.row as CustomInvoiceItemDTO).price)} ${currency}`}
                size="small"
                sx={priceChipSx}
            />
        ),
    },
];

export const LinkedInvoiceItems: FC<LinkedInvoiceItemsProps> = ({items, currency}) => {
    const intl = useIntl();
    const rows = items ?? [];

    const tableElement = rows.length ? (
        <Table
            rows={rows}
            hideFooter
            getRowId={(row) => row.id}
            getRowHeight={() => 'auto'}
            columns={columns(currency ?? CurrencyEnum.CHF)}
            sx={tableSx}
        />
    ) : (
        <EmptyTable/>
    );

    return (
        <Box>
            <TableLayout
                hideFooter
                toolbar={(
                    <FilterBar
                        title={intl.formatMessage({id: 'invoiceLinkedItems'})}
                        icon={<ListAltOutlined/>}
                        subtitle={intl.formatMessage({id: 'rowsFound'}, {count: rows.length})}
                    />
                )}
            >
                <ResponsiveTable
                    desktopElement={tableElement}
                    responsiveElement={rows.length ? (
                        <CardList>
                            {rows.map((item) => (
                                <LinkedInvoiceItemCard
                                    key={item.id}
                                    item={item}
                                    currency={currency ?? CurrencyEnum.CHF}
                                />
                            ))}
                        </CardList>
                    ) : (
                        <EmptyTable/>
                    )}
                />
            </TableLayout>
        </Box>
    );
};